import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { HeroBadge } from '../sections/HeroBadge.jsx';
import { INK, FG_2, PURPLE, SURFACE } from '../sections/theme.js';
import { useLocalizedPath } from '../../hooks/useLocalizedPath.jsx';

const HEADER_BG = ['#F8F2FF', '#F1F7F2', '#FFF3F4', '#FFF7EB', '#EBF6FE', '#F7F2FE', '#F0F7F5', '#FFF8EA', '#EDF4FE'];

const CARDS = [
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({ n, img: String(n), bg: HEADER_BG[n - 1] })),
    { n: 10, img: 'mail', bg: '#F1F7F2' },
    { n: 11, img: 'googleads', bg: '#FEF6E9' },
    { n: 12, img: 'analytics', bg: '#F5F0FF' },
    { n: 13, img: 'linkedin', bg: '#EBF3FE' },
    { n: 14, img: 'qr', bg: '#FDF0F1' },
];

// Feature cards that have a dedicated page — clicking the card deep-links there.
// Internal linking passes homepage authority to those pages; cards without a
// dedicated page stay non-clickable.
const CARD_LINKS = {
    1: 'employability',
    2: 'whiteLabel',
    3: 'marketing',
    4: 'signature',
    6: 'learningPaths',
    7: 'iee',
    8: 'integrations',
    9: 'massive',
    11: 'marketing',
};

export default function FeaturesGrid() {
    const { t } = useTranslation();
    const paths = useLocalizedPath();

    return (
        <Box component="section" sx={{ background: SURFACE.beige, py: { xs: 7, md: 10 } }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: 5.5 }}>
                    <HeroBadge label={t('home.features9.tag')} />
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '1.875rem', md: '2.375rem' },
                            fontWeight: 700,
                            lineHeight: 1.15,
                            letterSpacing: '-0.02em',
                            color: INK,
                            mb: 1.25,
                        }}
                    >
                        {t('home.features9.title')}
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 400, color: FG_2, lineHeight: 1.55 }}>
                        {t('home.features9.desc')}
                        <Box component="span" sx={{ color: PURPLE, fontWeight: 600 }}>
                            {t('home.features9.descAccent')}
                        </Box>
                    </Typography>
                </Box>

                <Grid container spacing={2.75}>
                    {CARDS.map(({ n, img, bg }) => (
                        <Grid key={n} size={{ xs: 12, md: 6 }}>
                            <Box
                                {...(CARD_LINKS[n] ? { component: Link, to: paths[CARD_LINKS[n]]() } : {})}
                                sx={{
                                    display: 'block',
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    cursor: CARD_LINKS[n] ? 'pointer' : 'default',
                                    background: '#fff',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    height: '100%',
                                    boxShadow: '0 10px 30px -22px rgba(20,4,77,.2)',
                                    transition: 'transform .18s ease, box-shadow .18s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 22px 46px -22px rgba(20,11,61,.24)',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        background: bg,
                                        height: 230,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={`/images/home/feat-${img}.webp`}
                                        alt={t(`home.features9.items.${n}.title`)}
                                        loading="lazy"
                                        sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                    />
                                </Box>
                                <Box sx={{ p: '26px 28px' }}>
                                    <Typography
                                        component="h3"
                                        sx={{ fontSize: '1.3125rem', fontWeight: 700, color: INK, mb: 1.25 }}
                                    >
                                        {t(`home.features9.items.${n}.title`)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '0.875rem',
                                            fontWeight: 400,
                                            lineHeight: 1.55,
                                            color: '#54515f',
                                        }}
                                    >
                                        {t(`home.features9.items.${n}.desc`)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
}
